import React, { useState, useEffect, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { RootDispatch, RootState } from "../../store";
import { fetchUserNames } from "../../store/slices/userSlice";
import { StylesConfig } from "react-select";

interface UserOption {
  value: string; // user._id
  label: string; // user.username
}

interface UserAsyncSelectProps {
  value?: string | null;
  onChange: (
    user:
      | {
          _id: string;
          username: string;
        }
      | null
      | undefined,
  ) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
}

// Custom modern styling matching modern design systems (Shadcn UI / Tailwind style)
const customStyles: StylesConfig<UserOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px", // Mobile touch-target guideline size
    borderRadius: "0.5rem", // 8px / rounded-lg
    borderColor: state.isFocused ? "#3b82f6" : "#e5e7eb", // blue-500 : gray-200
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.15)" : "none",
    backgroundColor: state.isDisabled ? "#f3f4f6" : "#ffffff",
    paddingLeft: "4px",
    paddingRight: "4px",
    cursor: "pointer",
    fontSize: "0.95rem", // 15px to prevent iOS auto-zoom on mobile inputs
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af", // gray-400
  }),
  input: (base) => ({
    ...base,
    color: "#111827", // gray-900
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "0.5rem",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #f3f4f6",
    overflow: "hidden",
    zIndex: 50,
  }),
  menuList: (base) => ({
    ...base,
    padding: "6px",
    maxHeight: "220px", // Comfortable scrolling list on mobile screens
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: "0.375rem",
    padding: "10px 12px", // Spacious padding for easy tapping on mobile
    margin: "2px 0",
    fontSize: "0.9rem",
    fontWeight: state.isSelected ? "600" : "400",
    backgroundColor: state.isSelected
      ? "#eff6ff" // blue-50
      : state.isFocused
        ? "#f9fafb" // gray-50
        : "transparent",
    color: state.isSelected ? "#2563eb" : "#374151", // blue-600 : gray-700
    cursor: "pointer",
    active: {
      backgroundColor: "#dbeafe",
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "12px",
  }),
  loadingMessage: (base) => ({
    ...base,
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "12px",
  }),
};

export const UserAsyncSelect: React.FC<UserAsyncSelectProps> = ({
  value,
  onChange,
  placeholder = "Search user...",
  isDisabled = false,
  className = "",
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { allUsersNames, loading } = useSelector(
    (state: RootState) => state.users,
  );

  const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);

  const options: UserOption[] = useMemo(() => {
    if (!Array.isArray(allUsersNames)) return [];
    return allUsersNames.map((user: { _id: string; username: string }) => ({
      value: user._id,
      label: user.username,
    }));
  }, [allUsersNames]);

  // FIXED: Do not reset selectedOption to null if value matches current selection
  useEffect(() => {
    if (value) {
      // 1. First try matching in current options list
      const matched = options.find((opt) => opt.value === value);

      if (matched) {
        setSelectedOption(matched);
      } else if (selectedOption && selectedOption.value === value) {
        // 2. Preserve local selection if ID hasn't changed
        return;
      }
    } else {
      // Clear if parent explicitly passed null/undefined/empty string
      setSelectedOption(null);
    }
  }, [value, options, selectedOption]);

  const loadOptions = useMemo(
    () =>
      debounce(
        (inputValue: string, callback: (options: UserOption[]) => void) => {
          dispatch(fetchUserNames(inputValue))
            .unwrap()
            .then((res: Array<{ _id: string; username: string }>) => {
              const formattedOptions = res.map((user) => ({
                value: user._id,
                label: user.username,
              }));
              callback(formattedOptions);
            })
            .catch(() => {
              callback([]);
            });
        },
        350,
      ),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      loadOptions.cancel();
    };
  }, [loadOptions]);

  const handleChange = (option: UserOption | null) => {
    setSelectedOption(option);
    onChange(option ? { _id: option.value, username: option.label } : null);
  };

  return (
    <div className={`w-full ${className}`}>
      <AsyncSelect
        cacheOptions
        defaultOptions
        isLoading={loading}
        value={selectedOption}
        loadOptions={loadOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        styles={customStyles}
        isClearable
      />
    </div>
  );
};
